import clsx from "clsx";
import React, { useState, useRef, useEffect, ReactNode } from "react";

import { useOnClickOutside, useEventListener } from "usehooks-ts";

export type DropdownProps<T extends string = string> = {
  id?: string;
  label?: string;
  size?: keyof typeof DropdownSize;
  placeholder: string;
  value?: T;
  options: DropdownItem<T>[];
  disabled?: boolean;
  errorMessage?: string;
  dataTestId?: string;
  handleValueChange: (value: DropdownItem<T>) => void;
  name?: string;
  classNameContainer?: string;
  optionsContainerClassName?: string;
};

export enum DropdownSize {
  small = "py-[7px]",
  medium = "py-[9px]",
  large = "py-[11px]",
}

export type DropdownItem<T extends string = string> = {
  label: ReactNode;
  description?: ReactNode;
  value: T;
  tailElement?: React.ReactNode;
};

export const Dropdown = <T extends string = string>({
  id = "select-id",
  label,
  value,
  options,
  placeholder,
  size = "small",
  disabled,
  errorMessage,
  dataTestId = "li-react-dropdown",
  handleValueChange,
  name,
  classNameContainer,
  optionsContainerClassName,
}: DropdownProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<T | null>(value ?? null);
  useEffect(() => {
    setSelectedValue(value ?? null);
  }, [value]);
  const ref = useRef(null);
  useOnClickOutside(ref, () => {
    setIsDropdownOpen(false);
  });

  const documentRef = useRef(document);
  useEventListener(
    "keyup",
    (e) => {
      if (e.key === "Escape") setIsDropdownOpen(false);
    },
    documentRef
  );

  const dropdownClasses = clsx(
    "w-full bg-white px-3 flex justify-between text-sm leading-4 rounded-lg",
    "border border-border-action hover:border-border-action-hover focus:border-border-action-focus active:border-border-action-focus",
    DropdownSize[size],
    {
      "bg-neutral-300 cursor-not-allowed": disabled,
      "border-red-700": errorMessage,
      "cursor-pointer": !disabled,
    }
  );

  const iconClass = clsx({
    "icon-lt-arrow-up-s-line": isDropdownOpen,
    "icon-lt-arrow-down-s-line": !isDropdownOpen,
    "text-neutral-400": disabled,
  });

  const valueTextClass = clsx("w-full", {
    "text-neutral-400": disabled || !selectedValue,
  });

  const handleOpenDropdown = () => {
    !disabled && setIsDropdownOpen((prev) => !prev);
  };

  const handleOptionClick = (item: DropdownItem<T>) => {
    if (disabled) return;
    setIsDropdownOpen(false);
    if (!item) return;
    if (item?.value) {
      setSelectedValue(item.value);
    }
    handleValueChange(item);
  };

  const getLabelByValue = (val: string) => {
    return options.find((opt) => opt.value === val)?.label;
  };
  return (
    <div className={clsx("flex flex-col relative", classNameContainer)} data-testid={dataTestId}>
      {label && (
        <label htmlFor={id} className="pb-2 m-0 text-xs font-semibold leading-5">
          {label}
        </label>
      )}
      <div ref={ref}>
        <div
          onClick={handleOpenDropdown}
          data-testid={`${dataTestId}-trigger`}
          aria-disabled={disabled}
          className={dropdownClasses}
        >
          <input type="text" className="hidden" id={id} value={selectedValue ?? ""} name={name} readOnly />
          <div className={valueTextClass}>
            <div className="text-neutral-800">{selectedValue ? getLabelByValue(selectedValue) : placeholder}</div>
          </div>
          <div>
            <i className={iconClass}></i>
          </div>
        </div>
        {isDropdownOpen && (
          <div
            data-testid={`${dataTestId}-options`}
            className={clsx(
              "absolute right-0 left-0 z-50 max-h-[400px] overflow-y-auto bg-white animate-slide-down origin-top-right mt-[6px] p-2 border border-solid rounded-sm border-neutral-300",
              optionsContainerClassName
            )}
          >
            <div>
              <div className="flex flex-col gap-1">
                {options.map((item) => (
                  <div
                    data-value={item?.value || ""}
                    onClick={() => handleOptionClick(item)}
                    key={item?.value || ""}
                    className={clsx(
                      "rounded-[6px] p-2 text-neutral-800 focus-visible:outline-hidden cursor-pointer hover:bg-neutral-100 text-sm ",
                      { "bg-blue-100": item?.value === selectedValue }
                    )}
                  >
                    <div className="flex flex-row items-center justify-between gap-2">
                      <div>{item.label}</div>
                      {item.value && <div className="flex items-center justify-center">{item.tailElement}</div>}
                    </div>

                    {item.description && <div>{item.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {errorMessage && <p className="pt-1 m-0 text-xs font-normal leading-5 text-red-700">{errorMessage}</p>}
    </div>
  );
};
